import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Space } from 'antd';
import styled from 'styled-components';
import { BookingsType, GridResponse } from '../../../types/types';
import { CustomTable } from '../../common/CustomTable';
import { getAllBookings } from '../../../api/requests/bookings';
import { useSnackbar } from '../../../contexts/SnackbarContext/SnackbarProvider';
import { useParams } from 'react-router-dom';
import {
  COLORS,
  DateFormatStrings,
  TEXT_SIZES,
} from '../../../utils/constants';
import { MailOutlined, PhoneOutlined } from '@ant-design/icons';
import {
  convertUtcToLocal,
  formatToHHMM,
  utcToLocalDate,
} from '../../../utils/dateUtils';
import ViewBookingDrawer from './ViewBookingDrawer';

interface BookingsSectionProps {
  onLoadingChange: (isLoading: boolean) => void;
}

const BookingsSection: React.FC<BookingsSectionProps> = ({
  onLoadingChange,
}) => {
  const snackbar = useSnackbar();
  const { facilityId } = useParams<{ facilityId: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<GridResponse<BookingsType[]>>();
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [selectedBookingId, setSelectedBookingId] = useState<number | null>(
    null,
  );
  const pageSize = 4;

  const handleOpenDrawer = (row: BookingsType) => {
    setOpenDrawer(true);
    setSelectedBookingId(row.bookingId);
  };

  const handleDrawerClose = () => {
    setOpenDrawer(false);
    setSelectedBookingId(null);
  };

  const columns = useMemo(
    () => [
      {
        title: 'Sport',
        dataIndex: 'sportName',
        key: 'sport',
      },
      {
        title: 'Court',
        dataIndex: 'courtName',
        key: 'court',
      },
      {
        title: 'Date & Time',
        dataIndex: 'bookingDate',
        key: 'dateTime',
        render: (text: Date) => {
          const value = utcToLocalDate(text);
          return (
            <Space direction="vertical" size={0}>
              <span>
                {convertUtcToLocal(text, DateFormatStrings.DD_MM_YYYY)}
              </span>
              <GrayColoredText>{formatToHHMM(value)}</GrayColoredText>
            </Space>
          );
        },
      },
      {
        title: 'Booked By',
        dataIndex: 'userName',
        key: 'bookedBy',
        render: (userName: BookingsType['userName'], record: BookingsType) => {
          return (
            <Space direction="vertical" size={0}>
              <span>{userName}</span>
              <Space>
                <IconWrapper>
                  <PhoneIconWrapper rotate={90} />
                </IconWrapper>
                <GrayColoredText>{record?.phoneNumber}</GrayColoredText>
              </Space>
              <Space>
                <IconWrapper>
                  <MailIconWrapper />
                </IconWrapper>
                <GrayColoredText>{record?.email}</GrayColoredText>
              </Space>
            </Space>
          );
        },
      },
      {
        title: 'Status',
        dataIndex: 'statusName',
        key: 'status',
      },
      {
        title: 'Action',
        dataIndex: 'action',
        key: 'action',
        render: (_: string, row: BookingsType) => (
          <ActionText onClick={() => handleOpenDrawer(row)}>View</ActionText>
        ),
      },
    ],
    [],
  );

  const getBookingsList = useCallback(
    async (facilityId: number, page = 1) => {
      try {
        setIsLoading(true);
        const response = await getAllBookings({
          facilityId,
          PageSize: pageSize,
          PageNumber: Math.max(0, page - 1),
        });
        if (response?.isSuccess && response?.value) {
          setData(response.value);
        }
      } catch (error: any) {
        snackbar.error(error.Error || 'Error fetching bookings');
      } finally {
        setIsLoading(false);
      }
    },
    [pageSize, snackbar],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      getBookingsList(Number(facilityId), page);
    },
    [facilityId, getBookingsList],
  );

  useEffect(() => {
    if (facilityId) {
      getBookingsList(Number(facilityId));
    }
  }, [facilityId, getBookingsList]);

  useEffect(() => {
    onLoadingChange(isLoading);
  }, [isLoading, onLoadingChange]);

  return (
    <div>
      <CustomTable
        columns={columns}
        dataSource={data?.rows && data.rows.length > 0 ? data.rows : []}
        loading={isLoading}
        rowKey={(record) => record.bookingId}
        pagination={{
          pageSize,
          total: data?.totalCount,
          onChange: (page) => handlePageChange(page),
        }}
      />
      {openDrawer && selectedBookingId && (
        <ViewBookingDrawer
          open={openDrawer}
          onClose={handleDrawerClose}
          bookingId={selectedBookingId}
          cancelSlot={false}
        />
      )}
    </div>
  );
};

const GrayColoredText = styled.span`
  color: ${COLORS.grey.secondary};
  font-size: ${TEXT_SIZES.XS};
`;

const IconWrapper = styled.div`
  background-color: ${COLORS.grey.secondaryLight};
  border-radius: 10px;
  height: 18px;
  width: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PhoneIconWrapper = styled(PhoneOutlined)`
  height: 10px;
  width: 10px;
`;

const MailIconWrapper = styled(MailOutlined)`
  height: 10px;
  width: 10px;
`;

const ActionText = styled.span`
  color: ${COLORS.blue.medium};
  cursor: pointer;
  text-decoration: none;
  &:hover {
    color: ${COLORS.blue.medium};
    text-decoration: underline;
  }
`;

export default BookingsSection;
